import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Loader2, AlertCircle } from 'lucide-react';
import Swal from 'sweetalert2';

const EditPackageModal = ({ packageData, onClose, onPackageUpdated ,fetchPackages}) => {
  const [formData, setFormData] = useState({
    priceTitle: '',
    priceDescription: '',
    price: '',
    priceDetail: '',
    tag: '',
    features: ['']
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPackageDetails();
  }, [packageData._id]);

  const fetchPackageDetails = async () => {
    try {
      setFetchLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/packages/get-package/${packageData._id}`,
        );

      if (response.data.success) {
        const pkg = response.data.data;
        setFormData({
          priceTitle: pkg.priceTitle || '',
          priceDescription: pkg.priceDescription || '',
          price: pkg.price || '',
          priceDetail: pkg.priceDetail || '',
          tag: pkg.tag || '',
          features: pkg.features && pkg.features.length > 0 ? pkg.features : ['']
        });
      }
    } catch (err) {
      console.error('Error fetching package details:', err);
      setError(err.response?.data?.message || 'Failed to load package details');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features: newFeatures
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.priceTitle.trim()) {
      setError('Package title is required');
      return;
    }
    if (!formData.price || formData.price <= 0) {
      setError('Valid price is required');
      return;
    }

    // Filter out empty features
    const filteredFeatures = formData.features.filter(f => f.trim() !== '');

    try {
      setLoading(true);
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/packages/update-package/${packageData._id}`,
        {
          ...formData,
          features: filteredFeatures
        },
        );

      if (response.data.success) {

        Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Package updated successfully!',
            timer: 2000,
            showConfirmButton: false,
        });
        fetchPackages();
        if (onPackageUpdated) {
          onPackageUpdated(response.data.data);
        }
        onClose();
      }
    } catch (err) {
      console.error('Error updating package:', err);
      setError(err.response?.data?.message || 'Failed to update package');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while fetching package details
  if (fetchLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#0bb6d9] mx-auto mb-4" />
            <p className="text-gray-600 text-lg">Loading package details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state if failed to fetch
  if (error && !formData.priceTitle) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Package</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
            >
              Close
            </button>
            <button
              onClick={fetchPackageDetails}
              className="flex-1 px-6 py-3 bg-[#0bb6d9] text-white rounded-lg font-semibold hover:bg-[#069ab7] transition"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0  bg-gradient-to-r from-[#0bb6d9] to-[#3cd7f6] text-white p-6 flex justify-between items-center rounded-t-2xl">
          <h2 className="text-2xl font-bold">Edit Package</h2>
          <button
            onClick={onClose}
            className="hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Package Title */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Package Title *
            </label>
            <input
              type="text"
              name="priceTitle"
              value={formData.priceTitle}
              onChange={handleInputChange}
              placeholder="e.g., Premium Plan"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              required
            />
          </div>

          {/* Package Description */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Description
            </label>
            <textarea
              name="priceDescription"
              value={formData.priceDescription}
              onChange={handleInputChange}
              placeholder="Brief description of the package"
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition resize-none"
            />
          </div>

          {/* Price and Detail */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Price (₹) *
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                placeholder="999"
                min="0"
                step="0.01"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Price Detail
              </label>
              <input
                type="text"
                name="priceDetail"
                value={formData.priceDetail}
                onChange={handleInputChange}
                placeholder="e.g., per month"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              />
            </div>
          </div>

          {/* Tag */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Tag (Optional)
            </label>
            <input
              type="text"
              name="tag"
              value={formData.tag}
              onChange={handleInputChange}
              placeholder="e.g., Most Popular"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
            />
          </div>

        {/* Features */}
<div>
  <label className="block text-gray-700 font-semibold mb-2">Features</label>
  <div className="space-y-3">
    {formData.features.map((feature, index) => (
      <div
        key={index}
        className="flex flex-col sm:flex-row gap-2 sm:items-center"
      >
        <input
          type="text"
          value={feature}
          onChange={(e) => handleFeatureChange(index, e.target.value)}
          placeholder={`Feature ${index + 1}`}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0bb6d9] focus:border-transparent outline-none transition w-full"
        />

        <button
          type="button"
          onClick={() => removeFeature(index)}
          disabled={formData.features.length === 1}
          className="px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto w-full"
        >
          <Trash2 className="w-5 h-5 mx-auto" />
        </button>
      </div>
    ))}
  </div>

  <button
    type="button"
    onClick={addFeature}
    className="mt-3 flex items-center gap-2 text-[#0bb6d9] hover:text-[#069ab7] font-semibold transition"
  >
    <Plus className="w-5 h-5" />
    Add Feature
  </button>
</div>


          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3     bg-[#0bb6d9]  hover:bg-[#069ab7]     text-white rounded-lg font-semibold  transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPackageModal;